import { type CallInfo, CallStatus } from '@compass/client/types/call';

/**
 * Whether a call carries a saved disposition — the numeric `disposition_type`
 * or the legacy label-only `disposition.type`. Mirrors the server-side
 * predicate (service/calls.go callHasDisposition) and the page-level
 * `liveHumanCallHasSavedDisposition` check.
 */
export function hasCallDisposition(call: CallInfo): boolean {
  return (
    (call.disposition_type !== undefined && call.disposition_type !== null) ||
    !!call.disposition?.type
  );
}

/**
 * Why a new human call cannot be placed on the case right now, or null when
 * it can.
 *
 * Mirrors the server guard (service/calls.go guardPreviousHumanCallDisposition):
 * the latest human call blocks a new one while it is still in progress, or
 * once it ended (completed/failed) without a disposition. Calls that never
 * became a conversation (not_started, scheduled, cancelled — e.g. an
 * abandoned dial) never block.
 */
export function getNewCallBlockReason(latestHumanCall: CallInfo | null | undefined): string | null {
  if (!latestHumanCall) return null;
  if (latestHumanCall.status === CallStatus.CALL_STATUS_IN_PROGRESS) {
    return 'A call is already in progress for this case.';
  }
  if (
    (latestHumanCall.status === CallStatus.CALL_STATUS_COMPLETED ||
      latestHumanCall.status === CallStatus.CALL_STATUS_FAILED) &&
    !hasCallDisposition(latestHumanCall)
  ) {
    return 'Set a disposition for the previous call attempt before starting a new call.';
  }
  return null;
}

/**
 * Why the human-call step cannot be marked completed right now, or null when it
 * can.
 *
 * Mirrors the server guard (service/calls.go
 * guardHumanCallDispositionBeforeCompletion): the latest human call blocks
 * completion once it has become a conversation (in progress, completed or
 * failed) without a disposition. A case with no human call at all, or whose
 * latest call never became a conversation (not_started, scheduled, cancelled —
 * e.g. an abandoned dial), never blocks. Terminating the step is deliberately
 * not guarded — it is the escape hatch.
 *
 * `workflowHasDispositions` is the whole rule's precondition and must come from
 * the disposition engine (`useDispositions(workflowType).data.length > 0`), not
 * from DISPOSITION_WORKFLOW_TYPES — that list only says whether the Confirm
 * dialog shows a picker. Workflows the engine has no catalog for
 * (benefit_verification, medicare_human, recall_scheduler, referral_scheduler)
 * can set a call disposition NOWHERE — the Review tab's dropdown renders empty
 * too — so blocking them would wedge the human-call step with Terminate as the
 * only exit. The server scopes the same way (workflowHasCallDispositions).
 */
export function getHumanCallCompletionBlockReason(
  latestHumanCall: CallInfo | null | undefined,
  workflowHasDispositions: boolean,
): string | null {
  if (!workflowHasDispositions) return null;
  if (!latestHumanCall) return null;
  if (
    latestHumanCall.status !== CallStatus.CALL_STATUS_IN_PROGRESS &&
    latestHumanCall.status !== CallStatus.CALL_STATUS_COMPLETED &&
    latestHumanCall.status !== CallStatus.CALL_STATUS_FAILED
  ) {
    return null;
  }
  if (hasCallDisposition(latestHumanCall)) return null;
  return 'Set a disposition for the call before marking the human call as completed.';
}
